import React, { Dispatch, SetStateAction, useRef } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import PlayPauseButton from "./PlayPauseButton";
import BackForwardButton from "./BackForwardButton";
import SnapshotButton from "./SnapshotButton";
import ShareButton from "./ShareButton";
import DownloadButton from "./DownloadButton";
import Timestamp from "./Timestamp";
import FullScreenButton from "./FullScreenButton";
import PlaybackSpeedButton from "./PlaybackSpeedButton";
import MotionEventsButton from "./MotionEventsButton";
import FishEyePlayerButton from "./FishEyePlayerButton";
import { MotionEventBarVisibility } from "../../models/motionEvent";
import { VideoInfo } from "../../models/videoInfo";
import { DeviceModel } from "../../../../Models/devices";
import { FISHEYE_VIEW_TYPES } from "../../../../../Pages/Cameras/components/PlaybackWebRTC/FishEyeGridSettings";

interface Props {
  serialNumber: string;
  realtimeSecondsElapsed: number;
  startDateIsoStr: string;
  videoInfo: VideoInfo | null;
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  changeDewarperView: (viewType: FISHEYE_VIEW_TYPES) => void;
  videoLength: number;
  videoWidth: number;
  timezone: string | undefined;
  videoTitle: string;
  isFullScreen: boolean;
  setIsFullScreen: Dispatch<SetStateAction<boolean>>;
  openShareDialog: () => void;
  motionEventBarVisibility: MotionEventBarVisibility;
  setMotionEventBarVisibility: Dispatch<SetStateAction<MotionEventBarVisibility>>;
  shouldDisplayShareButton: boolean;
  shouldDisplayDownloadButton: boolean;
  device?: DeviceModel;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    buttonWrapper: {
      padding: 8,
      paddingBottom: 4,
      all: "unset",
      outline: "revert",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "#416ca6",
        borderRadius: 4,
      },
    },
    controlBar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: 8,
    },
    lastItemSpacer: {
      width: 120,
    },
  })
);

const VideoControls = ({
  serialNumber,
  realtimeSecondsElapsed,
  startDateIsoStr,
  videoInfo,
  videoRef,
  videoLength,
  videoWidth,
  timezone,
  videoTitle,
  isFullScreen,
  setIsFullScreen,
  openShareDialog,
  motionEventBarVisibility,
  setMotionEventBarVisibility,
  shouldDisplayShareButton,
  shouldDisplayDownloadButton,
  device,
  changeDewarperView,
}: Props) => {
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement | null>(null);
  return (
    <div
      id="video-controls"
      className={classes.controlBar}
      style={{ width: videoWidth }}
      ref={containerRef}
    >
      <div>
        <PlayPauseButton videoRef={videoRef} buttonStyles={classes.buttonWrapper} />
        <BackForwardButton
          direction="back"
          videoRef={videoRef}
          videoLength={videoLength}
          buttonStyles={classes.buttonWrapper}
        />
        <BackForwardButton
          direction="forward"
          videoRef={videoRef}
          videoLength={videoLength}
          buttonStyles={classes.buttonWrapper}
        />
        <PlaybackSpeedButton
          videoRef={videoRef}
          buttonStyles={classes.buttonWrapper}
          containerRef={containerRef}
        />
      </div>
      <Timestamp
        realtimeSecondsElapsed={realtimeSecondsElapsed}
        startDateIsoStr={startDateIsoStr}
        timezone={timezone}
      />
      <div>
        <SnapshotButton
          videoRef={videoRef}
          buttonStyles={classes.buttonWrapper}
          videoTitle={videoTitle}
        />
        {shouldDisplayShareButton && (
          <ShareButton buttonStyles={classes.buttonWrapper} openShareDialog={openShareDialog} />
        )}
        {shouldDisplayDownloadButton && (
          <DownloadButton
            buttonStyles={classes.buttonWrapper}
            videoInfo={videoInfo}
            serialNumber={serialNumber}
            device={device}
          />
        )}
        <MotionEventsButton
          buttonStyles={classes.buttonWrapper}
          motionEventBarVisibility={motionEventBarVisibility}
          setMotionEventBarVisibility={setMotionEventBarVisibility}
          containerRef={containerRef}
        />
        <FishEyePlayerButton
          buttonStyles={classes.buttonWrapper}
          containerRef={containerRef}
          device={device}
          videoRef={videoRef}
          changeDewarperView={changeDewarperView}
        />
        <FullScreenButton
          device={device}
          isFullScreen={isFullScreen}
          setIsFullScreen={setIsFullScreen}
          buttonStyles={classes.buttonWrapper}
        />
      </div>
    </div>
  );
};

export default VideoControls;
